import { ReactNode, forwardRef } from "react";
import * as React from "react";
import cn from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { createBemBlockBuilder } from "@ebsco-ui/utils";

import "./control.scss";

export type ControlRef = HTMLInputElement;

type InputType = Omit<React.InputHTMLAttributes<HTMLInputElement>, "size">;

export type Size = "base" | "large";
export interface ControlProps extends InputType {
  icon: IconProp;
  label: ReactNode;
  type: string;
  size?: Size;
  className?: string;
  disabled?: boolean;
  error?: boolean;
}

const Control = forwardRef<ControlRef, ControlProps>(
  (
    {
      icon,
      label,
      type,
      size = "base",
      className,
      disabled = false,
      error = false,
      ...rest
    }: ControlProps,
    forwardedRef,
  ) => {
    const getBlocksWith = createBemBlockBuilder(["eb-control", className]);

    const getModifier = (blockElement: string): string => {
      const modifiedBlockElement = disabled
        ? `${blockElement}--disabled`
        : error
        ? `${blockElement}--error`
        : blockElement;

      return getBlocksWith(modifiedBlockElement);
    };

    return (
      <span className={getBlocksWith()} data-auto="control">
        <label
          className={cn(getBlocksWith("__label"), getModifier("__label"))}
          data-auto="control-label"
        >
          <input
            ref={forwardedRef}
            type={type}
            className={cn(
              getBlocksWith("__input"),
              getModifier("__input"),
              getBlocksWith(`__input--${size}`),
            )}
            data-auto="control-input"
            disabled={disabled}
            {...rest}
          />
          <span
            className={cn(
              getBlocksWith("__icon-wrapper"),
              getModifier("__icon-wrapper"),
            )}
            data-auto="control-label-icon-wrapper"
          >
            <FontAwesomeIcon
              data-auto="font-awesome-icon"
              className={cn(
                getBlocksWith("__icon"),
                getModifier("__icon"),
                getBlocksWith(`__icon--${size}`),
              )}
              icon={icon}
            />
          </span>
          <span
            className={cn(
              getBlocksWith("__label-text"),
              getBlocksWith(`__label-text--${size}`),
            )}
            data-auto="control-label-text"
          >
            {label}
          </span>
        </label>
      </span>
    );
  },
);

Control.displayName = "Control";

export default Control;
